import { useState } from 'react'
import { X, RefreshCw, Eye, EyeOff } from 'lucide-react'

// AS.Advertisers.create required: username, password, fullname, email,
//   company, companyurl
//   optional: sendwelcome, address, address2, city, state, country, postal, phone
// AS.Advertiser.editContact: cannot change username; updates fullname,
//   email, address fields, phone. (Password change is its own endpoint.)

// Auto-generated passwords: 16 chars, ASCII letters + digits + symbols.
// Must satisfy AdSpeed's documented min: 8 chars, at least one letter +
// one number.
function generatePassword(length = 16) {
  const lower = 'abcdefghijkmnpqrstuvwxyz'
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const digits = '23456789'
  const symbols = '!@#$%^&*-_+=?'
  const all = lower + upper + digits + symbols
  const buf = new Uint32Array(length)
  crypto.getRandomValues(buf)
  let out = ''
  // Guarantee one of each category in first 4 chars.
  out += lower[buf[0] % lower.length]
  out += upper[buf[1] % upper.length]
  out += digits[buf[2] % digits.length]
  out += symbols[buf[3] % symbols.length]
  for (let i = 4; i < length; i++) out += all[buf[i] % all.length]
  // Shuffle so the guaranteed-category chars aren't always at the start.
  return out.split('').sort(() => crypto.getRandomValues(new Uint32Array(1))[0] - 2147483648).join('')
}

export default function AdvertiserEditor({ initial = null, onClose, onSubmit }) {
  const isEdit = !!initial

  const [username, setUsername] = useState(initial?.username || initial?.Username || '')
  const [passwordMode, setPasswordMode] = useState('auto') // 'auto' | 'manual'
  const [password, setPassword] = useState(() => generatePassword())
  const [showPassword, setShowPassword] = useState(false)

  const [fullname, setFullname] = useState(initial?.fullname || initial?.Fullname || initial?.FullName || '')
  const [email, setEmail] = useState(initial?.email || initial?.Email || '')
  const [company, setCompany] = useState(initial?.company || initial?.Company || '')
  const [companyurl, setCompanyurl] = useState(initial?.companyurl || initial?.CompanyURL || initial?.Companyurl || '')

  const [address, setAddress] = useState(initial?.address || initial?.Address || '')
  const [city, setCity] = useState(initial?.city || initial?.City || '')
  const [state, setState] = useState(initial?.state || initial?.State || '')
  const [country, setCountry] = useState(initial?.country || initial?.Country || '')
  const [postal, setPostal] = useState(initial?.postal || initial?.Postal || '')
  const [phone, setPhone] = useState(initial?.phone || initial?.Phone || '')
  const [sendwelcome, setSendwelcome] = useState(true)

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  function regenerate() {
    setPassword(generatePassword())
  }

  function validateManualPassword(pw) {
    if (pw.length < 8) return 'Password must be at least 8 characters'
    if (!/[a-zA-Z]/.test(pw)) return 'Password must contain at least one letter'
    if (!/[0-9]/.test(pw)) return 'Password must contain at least one number'
    return null
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')

    if (!isEdit) {
      if (!username.trim()) return setError('Username is required')
      if (!password) return setError('Password is required')
      const pwErr = passwordMode === 'manual' ? validateManualPassword(password) : null
      if (pwErr) return setError(pwErr)
    }
    if (!fullname.trim()) return setError('Full name is required')
    if (!email.trim()) return setError('Email is required')
    if (!isEdit) {
      if (!company.trim()) return setError('Company is required')
      if (!companyurl.trim()) return setError('Company URL is required')
    }

    setSaving(true)
    try {
      const body = isEdit
        ? {
            fullname: fullname.trim(),
            email: email.trim(),
            address: address.trim() || undefined,
            city: city.trim() || undefined,
            state: state.trim() || undefined,
            country: country.trim() || undefined,
            postal: postal.trim() || undefined,
            phone: phone.trim() || undefined,
          }
        : {
            username: username.trim(),
            password,
            fullname: fullname.trim(),
            email: email.trim(),
            company: company.trim(),
            companyurl: companyurl.trim(),
            sendwelcome: sendwelcome ? 1 : 0,
            address: address.trim() || undefined,
            city: city.trim() || undefined,
            state: state.trim() || undefined,
            country: country.trim() || undefined,
            postal: postal.trim() || undefined,
            phone: phone.trim() || undefined,
            // List view shows reveal-once dialog only when admin used
            // auto-gen — manual passwords are admin's own choice and
            // don't need rebuilding.
            __revealPassword: passwordMode === 'auto' ? password : undefined,
          }
      await onSubmit(body)
    } catch (err) {
      setError(err.message)
    }
    setSaving(false)
  }

  function fieldLabel(text, required = false) {
    return (
      <label style={{ display: 'block', fontSize: '0.85rem', color: '#aaa', marginBottom: 4 }}>
        {text} {required && <span style={{ color: '#f59e0b' }}>*</span>}
      </label>
    )
  }

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#121212', border: '1px solid #333', borderRadius: 10,
          padding: 24, minWidth: 560, maxWidth: 720, width: '90%',
          maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: '1.2rem' }}>
            {isEdit ? 'Edit Advertiser Contact' : 'New Advertiser'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#aaa', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {!isEdit && (
            <>
              <div style={{ marginBottom: 12 }}>
                {fieldLabel('Username', true)}
                <input
                  autoFocus
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  className="zones-search"
                  style={{ width: '100%' }}
                  placeholder="e.g. acme_corp"
                />
                <div className="muted" style={{ fontSize: '0.72rem', marginTop: 4 }}>
                  Cannot be changed after creation. Used in advertiser login + AdSpeed's token recipe.
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                {fieldLabel('Password', true)}
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                  <button
                    type="button"
                    onClick={() => setPasswordMode('auto')}
                    className={passwordMode === 'auto' ? 'btn btn-primary' : 'sponsors-refresh-btn'}
                    style={{ fontSize: '0.78rem' }}
                  >
                    Auto-generate
                  </button>
                  <button
                    type="button"
                    onClick={() => { setPasswordMode('manual'); setPassword('') }}
                    className={passwordMode === 'manual' ? 'btn btn-primary' : 'sponsors-refresh-btn'}
                    style={{ fontSize: '0.78rem' }}
                  >
                    Manual
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="zones-search"
                    style={{ flex: 1, fontFamily: 'monospace' }}
                    readOnly={passwordMode === 'auto'}
                    placeholder={passwordMode === 'manual' ? 'Type at least 8 chars + 1 letter + 1 digit' : ''}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    className="sponsors-refresh-btn"
                    title={showPassword ? 'Hide' : 'Show'}
                  >
                    {showPassword ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                  {passwordMode === 'auto' && (
                    <button
                      type="button"
                      onClick={regenerate}
                      className="sponsors-refresh-btn"
                      title="Regenerate"
                    >
                      <RefreshCw size={13} />
                    </button>
                  )}
                </div>
                <div className="muted" style={{ fontSize: '0.72rem', marginTop: 4 }}>
                  {passwordMode === 'auto'
                    ? '16-char random password with mixed case, digits, symbols. Shown once after create.'
                    : 'AdSpeed requires at least 8 characters with at least one letter and one number.'}
                </div>
              </div>
            </>
          )}

          {isEdit && (
            <div style={{ marginBottom: 12 }}>
              {fieldLabel('Username')}
              <div className="muted" style={{ padding: '6px 10px', background: '#1a1a1a', borderRadius: 4, border: '1px solid #333', fontFamily: 'monospace' }}>
                {username}
              </div>
              <div className="muted" style={{ fontSize: '0.72rem', marginTop: 4 }}>
                Username cannot be changed after creation. Use the Change Password card on the advertiser detail page to rotate credentials.
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              {fieldLabel('Full name', true)}
              <input
                value={fullname}
                onChange={e => setFullname(e.target.value)}
                className="zones-search"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              {fieldLabel('Email', true)}
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="zones-search"
                style={{ width: '100%' }}
              />
            </div>
          </div>

          {!isEdit && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                {fieldLabel('Company', true)}
                <input
                  value={company}
                  onChange={e => setCompany(e.target.value)}
                  className="zones-search"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                {fieldLabel('Company URL', true)}
                <input
                  type="url"
                  value={companyurl}
                  onChange={e => setCompanyurl(e.target.value)}
                  className="zones-search"
                  style={{ width: '100%' }}
                  placeholder="https://example.com"
                />
              </div>
            </div>
          )}

          <details style={{ marginBottom: 12 }}>
            <summary style={{ cursor: 'pointer', color: '#aaa', fontSize: '0.85rem', marginBottom: 6 }}>
              Address (optional)
            </summary>
            <div style={{ marginTop: 8 }}>
              <div style={{ marginBottom: 8 }}>
                {fieldLabel('Street')}
                <input value={address} onChange={e => setAddress(e.target.value)} className="zones-search" style={{ width: '100%' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
                <div>
                  {fieldLabel('City')}
                  <input value={city} onChange={e => setCity(e.target.value)} className="zones-search" style={{ width: '100%' }} />
                </div>
                <div>
                  {fieldLabel('State')}
                  <input value={state} onChange={e => setState(e.target.value)} className="zones-search" style={{ width: '100%' }} />
                </div>
                <div>
                  {fieldLabel('Postal')}
                  <input value={postal} onChange={e => setPostal(e.target.value)} className="zones-search" style={{ width: '100%' }} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  {fieldLabel('Country')}
                  <input value={country} onChange={e => setCountry(e.target.value)} className="zones-search" style={{ width: '100%' }} />
                </div>
                <div>
                  {fieldLabel('Phone')}
                  <input value={phone} onChange={e => setPhone(e.target.value)} className="zones-search" style={{ width: '100%' }} />
                </div>
              </div>
            </div>
          </details>

          {!isEdit && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: '#ddd' }}>
                <input
                  type="checkbox"
                  checked={sendwelcome}
                  onChange={e => setSendwelcome(e.target.checked)}
                />
                Send AdSpeed's welcome email to the advertiser
              </label>
            </div>
          )}

          {error && (
            <div className="sponsors-status error" style={{ marginBottom: 12 }}>{error}</div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" onClick={onClose} className="sponsors-refresh-btn">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn btn-primary">
              {saving ? 'Saving…' : (isEdit ? 'Save' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
